import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowDown, ArrowUpRight, Check, Phone, Mail, ShieldCheck, Landmark, CalendarDays } from 'lucide-react';
import { Header, Footer } from '@/components/public';
import { SEOHead } from '@/components/seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { UsEnquiryModal } from '@/components/public/us/UsEnquiryModal';
import { UsSectionHeading, UsEnquireButton } from '@/components/public/us/UsElements';
import { SITE_ORIGIN } from '@/components/public/us/usContent';
import '@/components/public/uk/uk.css';

const PHONE = '+1 (205) 624-5819';
const PHONE_HREF = 'tel:+12056245819';
const EMAIL = 'hi@starteaseai.com';
const CANONICAL = `${SITE_ORIGIN}/usa/payroll/`;

const CTA = 'Request a consultation';

const INCLUDED = [
  ['Full-service payroll', 'We file and pay federal, state and local payroll taxes on your behalf.'],
  ['Automated payroll runs', 'Wages, withholdings and deposits calculated every cycle, on schedule.'],
  ['Free direct deposit', 'Paid straight to employee accounts, with a paper-check option available.'],
  ['W-2 and 1099-NEC e-filing', 'All year-end forms created, e-filed and issued to your people.'],
  ['New-hire reporting', 'Reported to the correct state agency, on time, for every hire.'],
  ['Multi-state payroll', 'Run payroll across all 50 states under one managed service.'],
  ['Garnishments & deductions', 'Court orders and deductions calculated and remitted correctly.'],
  ['Benefits & contributions', 'Health, 401(k) and workers’ comp reflected in each payroll.'],
  ['Employee self-service', 'A portal for pay stubs, W-2s and personal information.'],
  ['Employee onboarding', 'W-4 and I-9 collection handled as part of setup.'],
  ['Optional time & PTO', 'Time tracking and paid-time-off add-ons when you need them.'],
];

const TIERS = [
  {
    name: 'Single-state payroll',
    tag: 'Employees in one state',
    price: '$150',
    unit: '/ month',
    base: 'Up to 5 employees',
    extra: 'then $15 / employee / month',
  },
  {
    name: 'Multi-state payroll',
    tag: 'Employees across two or more states',
    price: '$250',
    unit: '/ month',
    base: 'Up to 5 employees',
    extra: 'then $25 / employee / month',
  },
];

const ADDONS = [
  ['Payroll state registration', '$50 per state, one-time', 'Employer withholding and unemployment account setup when you start payroll in a new state.'],
  ['Year-end W-2 package', '$50 flat for 1–5 staff', 'Then +$10 per additional W-2. Filed with the SSA and issued to employees.'],
  ['Contractor 1099-NEC', '$10 per form', 'Created, e-filed with the IRS and issued to each contractor.'],
];

const OBLIGATIONS = [
  ['01', 'Each payroll run', '', [
    'Calculate and withhold federal income tax, Social Security and Medicare.',
    'Apply the employer FICA match.',
    'Apply state — and any local — income-tax withholding.',
  ]],
  ['02', 'Monthly', 'or semiweekly, per the IRS lookback schedule', [
    'Federal tax deposits via EFTPS — withheld income tax, Social Security and Medicare.',
    'State income-tax withholding deposits, at the frequency each state sets.',
  ]],
  ['03', 'Quarterly', '', [
    'Form 941 — Employer’s Quarterly Federal Tax Return (IRS).',
    'FUTA deposit when liability exceeds $500 (IRS, via EFTPS).',
    'State withholding return, where the state requires one.',
    'State Unemployment Insurance (SUI/SUTA) wage and contribution report.',
  ]],
  ['04', 'Annual', '', [
    'Form 940 — Employer’s Annual Federal Unemployment (FUTA) Return (IRS).',
    'W-2 / W-3 — wage reporting to the SSA and employee copies (due Jan 31).',
    '1099-NEC / 1096 — contractor reporting to the IRS and contractor copies (due Jan 31).',
    'State annual withholding reconciliation and state W-2 copies.',
  ]],
  ['05', 'Ongoing / as needed', '', [
    'New-hire reporting to the state, typically within ~20 days of hire.',
    'Local, city or county payroll and occupational taxes where they apply.',
  ]],
];

const STEPS = [
  ['Discovery call', 'We map your states, headcount and pay schedule, then confirm your flat fee.'],
  ['Secure setup', 'You invite us into your payroll software and keep ownership. The software bills to your card directly.'],
  ['Hands-free payroll', 'We run every cycle and file every federal, state and local obligation on schedule.'],
];

const FAQS = [
  ['What’s the difference between single-state and multi-state pricing?', 'Single-state pricing ($150/month for up to 5 employees, then $15 each) is for teams working in one state. Multi-state pricing ($250/month for up to 5 employees, then $25 each) covers employees across two or more states, because each additional state adds its own deposits, withholding returns and unemployment filings to manage.'],
  ['Do you file in all 50 states?', 'Yes. We run and file payroll in all 50 states. When you hire in a new state, we complete the one-time payroll state registration ($50 per state) to open your employer withholding and unemployment accounts before the first run.'],
  ['What software do I need, and who pays for it?', 'You keep your own payroll software and remain the account owner. The software subscription is billed directly to your card at cost — we never mark it up. Our flat fee covers the managed service: running payroll and filing every obligation for you.'],
  ['Do you handle contractors and 1099s?', 'Yes. We create and e-file 1099-NEC forms ($10 per form) with the accompanying 1096 summary, and issue contractor copies by the January 31 deadline.'],
  ['What happens if I submit data late?', 'Submit clean payroll data at least 5 business days before a deadline and the filing is fully covered by your flat fee. With 4 business days or fewer, an urgent manual override is required: a $50 rush fee applies and the Tax Filing Guarantee is voided for that filing, so late state fees aren’t covered.'],
  ['Can you take over an existing payroll mid-year?', 'Yes. We can take over at any point in the year. During onboarding we reconcile your year-to-date wages, deposits and prior filings so your quarterly returns and year-end W-2s stay accurate.'],
];

const NAV = [
  ['included', 'Included'],
  ['pricing', 'Pricing'],
  ['obligations', 'Obligations'],
  ['guarantee', 'Guarantee'],
  ['onboarding', 'Onboarding'],
  ['questions', 'FAQs'],
];

const card = { border: '1px solid var(--uk-line)', background: 'var(--uk-panel)', borderRadius: 12, padding: '28px 26px' };

export default function USAManagedPayrollPage() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash === '#enquiry') setEnquiryOpen(true);
    else if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [location.hash]);

  const enquire = () => setEnquiryOpen(true);
  const changeOpen = (value) => { setEnquiryOpen(value); if (!value && location.hash === '#enquiry') navigate(location.pathname, { replace: true }); };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Managed Payroll Services',
    serviceType: 'Full-service managed payroll',
    description: 'Full-service managed payroll for US businesses — we file and pay every federal, state and local payroll obligation on time, backed by a Tax Filing Guarantee, for a flat monthly fee.',
    url: CANONICAL,
    areaServed: 'US',
    provider: { '@type': 'Organization', name: 'StartEase Agent', url: SITE_ORIGIN, telephone: '+1-205-624-5819', email: EMAIL },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
      { '@type': 'ListItem', position: 2, name: 'United States', item: `${SITE_ORIGIN}/usa/` },
      { '@type': 'ListItem', position: 3, name: 'Managed Payroll', item: CANONICAL },
    ],
  };

  return (
    <div className="uk-page" data-testid="us-payroll-page">
      <SEOHead
        title="Managed Payroll Services (US) — Full-Service, Done-for-You | StartEase Agent"
        description="Full-service managed payroll for US businesses. We file and pay every federal, state and local payroll tax on time, backed by our Tax Filing Guarantee — flat monthly fees, all 50 states."
        canonical={CANONICAL}
      >
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </SEOHead>
      <Header />

      <main>
        {/* 1. HERO */}
        <section className="uk-hero" data-testid="payroll-hero">
          <div className="uk-wrap" style={{ paddingBlock: '56px 64px' }}>
            <nav className="uk-breadcrumb" aria-label="Breadcrumb" data-testid="payroll-breadcrumb">
              <Link to="/" data-testid="payroll-breadcrumb-home">Home</Link><span>/</span>
              <Link to="/usa/" data-testid="payroll-breadcrumb-usa">United States</Link><span>/</span>
              <span aria-current="page">Managed Payroll</span>
            </nav>
            <div className="uk-hero-copy" style={{ maxWidth: 760 }}>
              <div className="uk-eyebrow">United States<span className="uk-sep" />Managed payroll</div>
              <h1 data-testid="payroll-hero-title">Payroll, filed and paid — <em>end to end.</em></h1>
              <p data-testid="payroll-hero-description">
                Full-service managed payroll for US businesses. We run every cycle and file and pay every
                federal, state and local payroll obligation on time — the same scope as Gusto, delivered
                as a done-for-you service on flat monthly fees.
              </p>
              <div className="uk-hero-actions">
                <UsEnquireButton onClick={enquire} testId="payroll-hero-cta">{CTA}</UsEnquireButton>
                <a href="#pricing" className="uk-text-link" data-testid="payroll-hero-pricing">See pricing<ArrowDown size={16} /></a>
              </div>
              <span className="uk-hero-footnote" data-testid="payroll-hero-footnote">
                Certified QuickBooks ProAdvisors <span> / </span> All 50 states <span> / </span> Tax Filing Guarantee
              </span>
            </div>
          </div>
        </section>

        {/* Trust facts */}
        <div className="uk-facts-band"><div className="uk-wrap uk-facts" data-testid="payroll-facts">
          {[[ShieldCheck, 'Certified ProAdvisors', 'Payroll run by Certified QuickBooks ProAdvisors.'],
            [Landmark, 'Every obligation filed', 'Federal, state and local payroll taxes filed and paid for you.'],
            [CalendarDays, 'Tax Filing Guarantee', 'On-time filings covered by your flat fee — details below.']].map(([Icon, title, text], i) => (
            <div key={title} data-testid={`payroll-fact-${i}`}><Icon size={23} strokeWidth={1.4} /><div><strong>{title}</strong><p>{text}</p></div></div>
          ))}
        </div></div>

        {/* Section nav */}
        <nav className="uk-wrap uk-section-nav" aria-label="On this page" data-testid="payroll-section-nav">
          {NAV.map(([id, text], i) => <a key={id} href={`#${id}`} data-testid={`payroll-nav-${id}`}><span>0{i + 1}</span>{text}</a>)}
        </nav>

        {/* 2. WHAT'S INCLUDED */}
        <section id="included" className="uk-section uk-wrap" data-testid="payroll-included-section">
          <UsSectionHeading id="payroll-included" number="01" eyebrow="Full-service scope" title="Everything payroll needs, handled." text="The same end-to-end scope you’d expect from Gusto — delivered as a managed, done-for-you service." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '18px 40px', marginTop: 10 }} data-testid="payroll-included-grid">
            {INCLUDED.map(([title, text], i) => (
              <div key={title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBlock: 14, borderTop: '1px solid var(--uk-line)' }} data-testid={`payroll-included-${i}`}>
                <Check size={18} style={{ color: 'var(--uk-green)', flexShrink: 0, marginTop: 4 }} />
                <div><strong style={{ display: 'block', color: '#fff', fontSize: 15, fontWeight: 600 }}>{title}</strong><p style={{ color: 'var(--uk-ink)', fontSize: 13.5, marginTop: 4 }}>{text}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* 3. PRICING */}
        <section id="pricing" className="uk-soft-band"><div className="uk-wrap uk-section" data-testid="payroll-pricing-section">
          <div className="uk-heading-row">
            <UsSectionHeading id="payroll-pricing" number="02" eyebrow="Flat, transparent fees" title="Pricing you can plan around." text="These are StartEase management fees. Your payroll software subscription is billed to you directly, at cost — never marked up." />
            <UsEnquireButton secondary onClick={enquire} testId="payroll-pricing-cta">{CTA}</UsEnquireButton>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 20, marginTop: 14 }} data-testid="payroll-tiers">
            {TIERS.map((t, i) => (
              <div key={t.name} style={card} data-testid={`payroll-tier-${i}`}>
                <span className="uk-eyebrow" style={{ color: '#6ee7b7' }}>{t.tag}</span>
                <h3 style={{ color: '#fff', fontSize: 22, margin: '12px 0 16px' }}>{t.name}</h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 40, color: '#fff', lineHeight: 1 }}>{t.price}</span>
                  <span style={{ color: 'var(--uk-ink)', fontSize: 14 }}>{t.unit}</span>
                </div>
                <p style={{ color: 'var(--uk-ink)', fontSize: 13.5, marginTop: 14 }}>{t.base}</p>
                <p style={{ color: '#6ee7b7', fontSize: 13.5, marginTop: 2 }}>{t.extra}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 0, marginTop: 28, borderTop: '1px solid var(--uk-line)' }} data-testid="payroll-addons">
            {ADDONS.map(([title, price, text], i) => (
              <div key={title} style={{ paddingTop: 26, paddingRight: 24 }} data-testid={`payroll-addon-${i}`}>
                <strong style={{ display: 'block', color: '#fff', fontSize: 16 }}>{title}</strong>
                <span style={{ display: 'block', color: '#6ee7b7', fontSize: 14, marginTop: 6 }}>{price}</span>
                <p style={{ color: 'var(--uk-ink)', fontSize: 13, marginTop: 8 }}>{text}</p>
              </div>
            ))}
          </div>
        </div></section>

        {/* 4. OBLIGATIONS */}
        <section id="obligations" className="uk-dark-band"><div className="uk-wrap uk-section" data-testid="payroll-obligations-section">
          <UsSectionHeading id="payroll-obligations" number="03" eyebrow="The credibility centerpiece" title="Federal and state obligations we handle." text="Grouped by how often each falls due. We manage the full cadence so nothing is missed." />
          <div style={{ marginTop: 32 }}>
            {OBLIGATIONS.map(([num, freq, note, items]) => (
              <div key={freq} className="pay-ob-row" data-testid={`payroll-ob-group-${num}`}>
                <div>
                  <span className="uk-obligation-number">{num}</span>
                  <h3 style={{ color: '#fff', fontSize: 19, marginTop: 6 }}>{freq}</h3>
                  {note && <p style={{ color: 'var(--uk-muted)', fontSize: 12.5, marginTop: 6 }}>{note}</p>}
                </div>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {items.map((li, i) => (
                    <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: 'var(--uk-ink)', fontSize: 13.5, lineHeight: 1.7 }} data-testid={`payroll-ob-${num}-${i}`}>
                      <Check size={16} style={{ color: 'var(--uk-green)', flexShrink: 0, marginTop: 4 }} /><span>{li}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="uk-fineprint" style={{ marginTop: 24 }} data-testid="payroll-ob-disclaimer">
            Indicative obligations, not an individual filing calendar. Exact deposit frequency, state returns and local taxes depend on your entity, states and IRS lookback status.
          </p>
          <a className="uk-text-link" href="https://www.irs.gov/businesses/small-businesses-self-employed/employment-taxes" target="_blank" rel="noopener noreferrer" data-testid="payroll-ob-source">Check official IRS employment-tax guidance <ArrowUpRight size={15} /></a>
        </div></section>

        {/* 5. TAX FILING GUARANTEE */}
        <section id="guarantee" className="uk-section uk-wrap" data-testid="payroll-guarantee-section">
          <UsSectionHeading id="payroll-guarantee" number="04" eyebrow="Our commitment" title="The Tax Filing Guarantee." />
          <div style={{ ...card, marginTop: 10, display: 'flex', gap: 20, alignItems: 'flex-start' }} data-testid="payroll-guarantee-card">
            <ShieldCheck size={30} style={{ color: 'var(--uk-green)', flexShrink: 0 }} strokeWidth={1.4} />
            <div>
              <p style={{ color: '#e2e8f0', fontSize: 15.5, lineHeight: 1.8 }}>
                Submit clean payroll data at least <strong style={{ color: '#6ee7b7', fontWeight: 600 }}>5 business days</strong> before the deadline and the filing is fully covered by your flat fee.
              </p>
              <p style={{ color: 'var(--uk-ink)', fontSize: 13.5, lineHeight: 1.8, marginTop: 14 }}>
                Data submitted <strong style={{ color: '#fff', fontWeight: 600 }}>4 business days or fewer</strong> before the deadline requires an urgent manual override: a <strong style={{ color: '#fff', fontWeight: 600 }}>$50 rush fee</strong> applies and the guarantee is voided for that filing, so late state fees aren’t covered.
              </p>
            </div>
          </div>
        </section>

        {/* 6. ONBOARDING */}
        <section id="onboarding" className="uk-soft-band"><div className="uk-wrap uk-section" data-testid="payroll-onboarding-section">
          <UsSectionHeading id="payroll-onboarding" number="05" eyebrow="From sign-up to hands-free" title="Three steps to managed payroll." />
          <ol className="pay-steps" data-testid="payroll-steps">
            {STEPS.map(([title, text], i) => (
              <li key={title} data-testid={`payroll-step-${i + 1}`}><span className="pay-step-num">0{i + 1}</span><h3>{title}</h3><p>{text}</p></li>
            ))}
          </ol>
        </div></section>

        {/* 7. FAQ */}
        <section id="questions" className="uk-section uk-wrap" data-testid="payroll-faq-section">
          <UsSectionHeading id="payroll-faq" number="06" eyebrow="Questions, answered" title="Payroll, made clear." text="The questions we hear most from founders, sellers and finance teams." />
          <Accordion type="single" collapsible className="uk-faqs" data-testid="payroll-faqs">
            {FAQS.map(([question, answer], i) => (
              <AccordionItem key={question} value={`question-${i}`} data-testid={`payroll-faq-item-${i}`}>
                <AccordionTrigger data-testid={`payroll-faq-trigger-${i}`}>{question}</AccordionTrigger>
                <AccordionContent data-testid={`payroll-faq-answer-${i}`}>{answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* 9. CLOSING CTA */}
        <section className="uk-section uk-wrap uk-closing" data-testid="payroll-closing-section">
          <div>
            <span className="uk-eyebrow">Done-for-you payroll, on flat fees.</span>
            <h2>Let’s get your payroll running.</h2>
            <p>Tell us your states, headcount and pay schedule — we’ll confirm your flat fee and take it from there.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 18, fontSize: 14 }}>
              <a href={PHONE_HREF} className="uk-text-link" data-testid="payroll-closing-phone"><Phone size={15} />{PHONE}</a>
              <a href={`mailto:${EMAIL}`} className="uk-text-link" data-testid="payroll-closing-email"><Mail size={15} />{EMAIL}</a>
            </div>
          </div>
          <UsEnquireButton onClick={enquire} testId="payroll-closing-cta">{CTA}</UsEnquireButton>
        </section>

        <div className="uk-wrap uk-editorial-note" data-testid="payroll-editorial-note">
          <p>General information, not legal or tax advice. We file and pay on the client’s behalf as a payroll service provider; we are not a law firm. Service scope, inclusions and fees are confirmed in writing before engagement, and employers remain responsible for their legal obligations.</p>
        </div>
      </main>

      <Footer />
      <UsEnquiryModal open={enquiryOpen} onOpenChange={changeOpen} structure="Existing US company support" />
    </div>
  );
}
